import React, { useState } from 'react';
import {
  Create,
  CreateBase,
  CreateProps,
  DateInput,
  required,
  ShowBase,
  Show,
  SimpleForm,
  TextInput,
  useTranslate,
  TextField,
  RichTextField,
  DateField,
  SimpleShowLayout,
  TopToolbar,
  EditButton,
  useCreate,
  ReferenceInput,
  useRecordContext,
  ReferenceField,
  Labeled,
  TabbedShowLayout,
  FormTab,
  Tab,
  ArrayField,
  ArrayFieldProps,
  SingleFieldList,
  NumberField,
  ChipField,
  TextFieldProps,
  useGetOne,
} from 'react-admin';
import { Client, Contract } from '../types';
import { Grid, Box, Typography, Stack, BoxProps, Chip, Alert } from '@mui/material';
import { LinesShow } from './lines/LinesShow';
import { ContractTransportSummary } from './ContractTransportSummary';
import ContractTopToolBar from './ContractTopToolBar';
import { TaxesArrayField } from './TaxesArrayField';
import { SubTotalField } from './SubTotalField';
import { PriceField } from '../components/PriceField';
import ContractLinkedInvoicesList from './ContractLinkedInvoicesList';
import { AddPaymentButton } from '../payments/AddPaymentButton';
import ContractLinkedPaymentsList from './ContractLinkedPaymentsList';
import { Divider } from '@mui/material';
import { numberFormatConfiguration, locale } from '../components/numberFormatConfiguration';
import { QuickbooksPriceField } from '../components/QuickbooksPriceField';
import { TaxSummary } from './TaxSummary';
import ContractLinkedCreditMemosList from './ContractLinkedCreditMemosList';
import { LegacyLinesShow } from './lines/LegacyLinesShow';
import { LegacyLinesTotal } from './lines/LegacyLinesTotal';
import { useDefineAppLocation } from "@react-admin/ra-navigation";
import { ContractShowTitle } from './ContractShowTitle';

const PreneedRegistryWarning = () => {
  const record = useRecordContext<Contract>();
  
  const { data: funeral, isLoading } = useGetOne(
    'funerals',
    { id: record?.funeral_id },
    { enabled: !!record?.funeral_id }
  );

  if (isLoading || !funeral) return null;

  // Don't show warning if preneed_registry is true
  if (funeral.preneed_registry || record.is_preneed) return null;

  return (
    <Alert 
      severity="warning" 
      sx={{ mx: 2, mb: 2 }}
    >
      <Typography variant="body2">
        <strong>Warning:</strong> Preneed Registry is not complete. Please check the registry before sending this contract.
      </Typography>
    </Alert>
  );
};

/** T-224: the QuickBooks estimate syncs in the background; say so when it went wrong. */
const EstimateSyncProblem = () => {
  const record = useRecordContext<Contract>();
  const problem = (record as any)?.estimate_sync_problem;
  if (!problem) return null;

  const dollars = (cents: number) => (cents / 100).toFixed(2);
  return (
    <Alert severity="warning" sx={{ mx: 2, mb: 2 }}>
      <Typography variant="body2">
        {problem.kind === 'total_mismatch'
          ? `QuickBooks totals this estimate at $${dollars(problem.quickbooks)}, but the family's contract says $${dollars(problem.ours)}. The family's total was not changed. Check before taking payment.`
          : `This contract could not be synced to QuickBooks: ${problem.message}. The family's total is not affected. Saving the contract tries again.`}
      </Typography>
    </Alert>
  );
};

const MonthlyPaymentDisplay = () => {
  const record = useRecordContext<Contract>();
  
  if (!record || !record.is_preneed || !record.is_monthly || !record.monthly_payment_amount) {

    return null;
  }

  const formattedAmount = Intl.NumberFormat(locale, numberFormatConfiguration).format(
    record.monthly_payment_amount
  );

  const formattedDownPayment = record.down_payment_amount
    ? Intl.NumberFormat(locale, numberFormatConfiguration).format(record.down_payment_amount)
    : null;

  return (
    <Box sx={{ mb: 2, mx: 2 }}>
      <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
        Monthly Payment Information
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        <strong>Payment Term:</strong> {record.payment_term || 36} months ({(record.payment_term || 36) === 12 ? '1 year' : '3 years'})
      </Typography>
      {formattedDownPayment && (
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Down Payment:</strong> {formattedDownPayment}
        </Typography>
      )}
      <Typography variant="body1">
        <strong>Calculated Monthly Payment Amount:</strong> {formattedAmount}
      </Typography>
    </Box>
  );
};

const SwitchLines = () => {
  const record = useRecordContext();

  if (!record) return null;

  if (record.legacy_lines?.lines?.length > 0) {
    return (
      <LegacyLinesShow source='legacy_lines.lines' />
    );
  }
  return (
    <>
      <LinesShow source='lines' />
      <ContractTransportSummary />
      <EstimateSyncProblem />
      <TaxSummary source="taxes" />
    </>

  );
}

const SwitchPrimaryClient = (props: TextFieldProps) => {
  const record = useRecordContext();

  if (!record) return null;

  if (record.legacy_lines?.lines?.length > 0) {
    return (
      <>
        <TextField source="legacy_lines.name" sx={{ mr: '10px' }} />
        <TextField source="legacy_lines.street" sx={{ mr: '10px' }} />
        <TextField source="legacy_lines.city" sx={{ mr: '10px' }} />
        <TextField source="legacy_lines.state" sx={{ mr: '10px' }} />
        <TextField source="legacy_lines.zip" sx={{ mr: '10px' }} />
        <TextField source="legacy_lines.relation" sx={{ mr: '10px' }} />
      </>


    );
  }
  return (

    <ReferenceField source="client_id" reference="clients" link="show" label="Client">
        <TextField source="name" />
    </ReferenceField>
    
    // <ReferenceField source="funeral_id" reference="funerals" label="Client" link={false}>
    //   <ReferenceField source="primary_client_id" reference="clients" link="show" label="Client">
    //     <TextField source="name" />
    //   </ReferenceField>
    // </ReferenceField>
  );
}

const SwitchContractDate = (props: TextFieldProps) => {
  const record = useRecordContext();

  if (!record) return null;

  if (record.legacy_lines?.lines?.length > 0) {
    return (
      <TextField source="legacy_lines.date" />
    );
  }
  return (
    <DateField
      source="issue_date"
      label="Contract Issue Date"
    />
  );
}

const ContractShowLayout = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  useDefineAppLocation("funerals.show.contracts.show", { record });

  return (
    <div>
      <Typography m={2} variant='h5'>
        <ContractShowTitle />
      </Typography>
      <PreneedRegistryWarning />
      <MonthlyPaymentDisplay />
      <Divider sx={{mx: 2, mb: 1}}/>
        <Grid container spacing={2} mt={-1}>
          <Grid item xs={10}>
              <SimpleShowLayout>
                <ReferenceField source="funeral_id" reference="funerals" link="show">
                  <TextField source="id"  />
                  </ReferenceField>
                  <ReferenceField source="funeral_id" reference="funerals" label="Deceased Client" link={false}>
                    <ReferenceField source="deceased_client_id" reference="clients">
                        <TextField source="name" />
                    </ReferenceField>
                </ReferenceField>
                <SwitchPrimaryClient label="Contract Client" />
                <ReferenceField source="funeral_id" reference="funerals" label="Owner" link={false}>
                    <ReferenceField source="owner_id" reference="profiles">
                        <TextField source="name" />
                    </ReferenceField>
                </ReferenceField>
                <SwitchContractDate label="Contract Issue Date" />
                <RichTextField source="memo" label="Special Instructions" />
              </SimpleShowLayout>
          </Grid>
          {record?.legacy_lines?.lines?.length > 0 && record?.is_preneed ?
          <Grid item xs={10}>
            <SimpleShowLayout>
              <TextField source="legacy_lines.Subtotal"  label="Subtotal"/>
              <TextField source="legacy_lines.FedTax" label="FedTax" />
              <TextField source="legacy_lines.ProvTax"  label="ProvTax"/>
              <TextField source="legacy_lines.Total"  label="Total" />
              <TextField source="legacy_lines.Deposit" label="Deposited into Trust" />
            </SimpleShowLayout>
          </Grid>
          : null }
          <Grid item xs={12} mx={2}>
              <SwitchLines />
              <LinkedLists />
        </Grid>
      </Grid>
    </div>
  )

}

const ContractShow = () => {

  return (
    <Show actions={<ContractTopToolBar />} sx={{ mb: 4 }}>
      <ContractShowLayout />
    </Show>
  );
}

const LinkedLists = () => {
  const record = useRecordContext();

  if (!record) return null;


  return (
    <>
      <ContractLinkedPaymentsList />
      <ContractLinkedInvoicesList />
      <ContractLinkedCreditMemosList />
    </>
  );

}



export default ContractShow;